import { Rating } from "@smastrom/react-rating";
import { useState, useRef, useEffect } from "react";
import { sendReview, getReview } from "../../../services/AthleteService";
import { useAxiosInstance } from "../../../services/Interceptor";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import "@smastrom/react-rating/style.css";
import PremiumFeatureModal from "../../../components/PremiumFeatureModal";
import FreeTrialModal from "../../../components/FreeTrialModal";
import { StartFreeTrial } from "../../../services/PaymentService";
import {
  clearRegisterData,
  registerAccountStore,
} from "../../../store/slices/accountSlice";
import { setProfileAction } from "../../../store/AthleteActions";
import { validateSubscriptionAction } from "../../../store/userActions";
import { bumpMenuRefresh } from "../../../store/slices/athleteSlice";

export function AthleteReview({
  hasSubscriptionAccess = true,
  hasUsedFreeTrial = false,
}) {
  const [rating, setRating] = useState(1);
  const [loading, setLoading] = useState(false);
  const [reviewText, setReviewText] = useState("");
  const [openPremiumFeatureModal, setOpenPremiumFeatureModal] = useState(false);
  const [openTrialModal, setOpenTrialModal] = useState(false);
  const titleRef = useRef(null);
  const reviewRef = useRef(null);
  const formRef = useRef(null);
  const reviewContainerRef = useRef(null);
  const dismissedRef = useRef(false);
  const modalDismissTimeoutRef = useRef(null);
  const apiInstance = useAxiosInstance();
  const authUser = useAuthUser();
  const { user = {} } = authUser || {};
  const dispatch = useDispatch();
  const isReviewLocked = !hasSubscriptionAccess;
  const showFreeTrial =
    isReviewLocked && user?.role === "athlete" && !hasUsedFreeTrial;

  useEffect(() => {
    const getReviewUseEffect = async () => {
      try {
        setLoading(true);
        const response = await getReview(apiInstance, authUser?.user?.id);
        if (response.status === 200) {
          titleRef.current.value = response.data.reviewTitle || "";
          setReviewText(response.data.reviewText || "");
          setRating(response.data.numberOfStars || 1);
        }
      } catch (err) {
        toast.error(err.response);
      } finally {
        setLoading(false);
      }
    };
    getReviewUseEffect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      if (modalDismissTimeoutRef.current) {
        clearTimeout(modalDismissTimeoutRef.current);
      }
    },
    [],
  );

  const openSubscription = () => {
    const updatedData = {
      userId: user.id,
      role: "athlete",
      email: user.email,
      registered: true,
      eventId: 0,
    };
    dispatch(clearRegisterData());
    dispatch(registerAccountStore(updatedData));
    localStorage.setItem("registeredAccount", JSON.stringify(updatedData));
    localStorage.removeItem("subscriptionDetails");
    setTimeout(() => {
      window.location.href = "/register/subscription";
    }, 2000);
  };

  const handlePremiumFreeTrial = () => {
    setOpenPremiumFeatureModal(false);
    setOpenTrialModal(true);
  };

  const handlePremiumSubscribe = () => {
    setOpenPremiumFeatureModal(false);
    openSubscription();
  };

  const handlePremiumModalClose = () => {
    dismissedRef.current = true;
    setOpenPremiumFeatureModal(false);

    if (modalDismissTimeoutRef.current) {
      clearTimeout(modalDismissTimeoutRef.current);
    }

    modalDismissTimeoutRef.current = setTimeout(() => {
      reviewContainerRef.current?.focus();
      dismissedRef.current = false;
    }, 0);
  };

  const handleBlockedAction = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (dismissedRef.current) return;

    setOpenPremiumFeatureModal(true);
  };

  const handleRatingChange = (nextRating) => {
    if (isReviewLocked) {
      setOpenPremiumFeatureModal(true);
      return;
    }

    setRating(nextRating);
  };

  const handleReviewTextChange = (e) => {
    if (isReviewLocked) {
      handleBlockedAction(e);
      return;
    }

    setReviewText(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isReviewLocked) {
      handleBlockedAction(e);
      return;
    }

    if (reviewText.length > 209) {
      toast.error("The review cannot exceed 209 characters.");
      return;
    }

    try {
      setLoading(true);
      const data = {
        reviewTitle: titleRef.current.value,
        reviewText: reviewText,
        numberOfStars: rating,
      };
      await sendReview(apiInstance, authUser?.user?.id, data);
      toast.success("Review sent successfully.");
    } catch (err) {
      toast.error(err.response.data);
    } finally {
      setLoading(false);
    }
  };

  const handleInputFocus = (e) => {
    if (isReviewLocked && !dismissedRef.current) {
      e.target.blur();
      setOpenPremiumFeatureModal(true);
    }
  };

  const handleLockedControlKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      handleBlockedAction(e);
    }
  };

  const renderLockedControlOverlay = () => (
    <button
      type="button"
      aria-label="Review controls require a subscription"
      className="absolute inset-0 z-10 cursor-not-allowed bg-transparent"
      onClick={handleBlockedAction}
      onKeyDown={handleLockedControlKeyDown}
    />
  );

  return (
    <div
      ref={reviewContainerRef}
      tabIndex={-1}
      role="region"
      aria-label="Review form"
      className="mt-4 outline-none"
    >
      <PremiumFeatureModal
        open={openPremiumFeatureModal}
        onClose={handlePremiumModalClose}
        title="Premium Feature"
        description="Submitting a review is locked. Start a free trial or subscribe now."
        onStartFreeTrial={handlePremiumFreeTrial}
        onSubscribe={handlePremiumSubscribe}
        showStartFreeTrial={showFreeTrial}
      />
      <FreeTrialModal
        open={openTrialModal}
        onClose={() => setOpenTrialModal(false)}
        user={user}
        onStoreSuccessChain={async () => {
          const result = await StartFreeTrial(apiInstance, user.id);
          if (result.success) {
            toast.success("Free trial started!", { position: "top-right" });
            setOpenTrialModal(false);
            await dispatch(setProfileAction(apiInstance, user.id));
            await dispatch(
              validateSubscriptionAction(apiInstance, user.role, user.id),
            );
            dispatch(bumpMenuRefresh());
          } else {
            toast.error("Failed to start free trial", {
              position: "top-right",
            });
          }
        }}
      />
      <div className="flex justify-end">
        <div className="relative">
          <Rating
            style={{ maxWidth: 180 }}
            value={rating}
            onChange={handleRatingChange}
            readOnly={isReviewLocked}
          />
          {isReviewLocked && renderLockedControlOverlay()}
        </div>
      </div>
      <form
        ref={formRef}
        className="relative flex flex-col space-y-3 mt-5"
        onSubmit={handleSubmit}
      >
        {isReviewLocked && renderLockedControlOverlay()}
        <input
          ref={titleRef}
          type="text"
          className={`border border-gray-300 p-2 w-full ${isReviewLocked ? "cursor-not-allowed bg-gray-100 text-gray-500" : ""}`}
          placeholder="Title"
          required
          maxLength={150}
          disabled={isReviewLocked}
          onFocus={handleInputFocus}
        />
        <textarea
          ref={reviewRef}
          className={`border border-gray-300 p-2 w-full ${isReviewLocked ? "cursor-not-allowed bg-gray-100 text-gray-500" : ""}`}
          rows={5}
          placeholder="Review"
          required
          maxLength={209}
          value={reviewText}
          onChange={handleReviewTextChange}
          onFocus={handleInputFocus}
          disabled={isReviewLocked}
          style={{
            maxHeight: "400px",
            overflow: "auto",
            resize: "vertical",
          }}
        ></textarea>
        <div className="flex justify-between">
          <span className="text-gray-500">
            {209 - reviewText.length} characters remaining
          </span>
          <button
            type="submit"
            className={`bg-blue-600 text-white uppercase rounded-md px-6 py-2 text-center hover:bg-blue-800 cursor-pointer mt-2 ${loading || isReviewLocked ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={loading || isReviewLocked}
          >
            {loading ? "Submitting" : "Submit Review"}
          </button>
        </div>
      </form>
    </div>
  );
}
